import { type ReactNode, useRef, useState } from 'react'

import AtomModal, { type AtomModalProps, type AtomModalType } from './AtomModal'

export type AtomModalOptions = Pick<
  AtomModalProps,
  'title' | 'okText' | 'cancelText' | 'danger' | 'width'
> & {
  content?: ReactNode
  onOk?: () => unknown
}

type AtomModalDialog = {
  id: number
  type: AtomModalType
  options: AtomModalOptions
  open: boolean
  loading: boolean
  resolve: (confirmed: boolean) => void
}

export type AtomModalHandle = {
  close: () => void
  result: Promise<boolean>
}

export type AtomModalApi = Record<
  AtomModalType,
  (options: AtomModalOptions) => AtomModalHandle
>

export const useAtomModal = () => {
  const [dialogs, setDialogs] = useState<AtomModalDialog[]>([])
  const nextIdRef = useRef(0)

  const updateDialog = (id: number, patch: Partial<AtomModalDialog>) =>
    setDialogs((current) =>
      current.map((dialog) =>
        dialog.id === id ? { ...dialog, ...patch } : dialog
      )
    )

  const removeDialog = (id: number) =>
    setDialogs((current) => current.filter((dialog) => dialog.id !== id))

  const openDialog =
    (type: AtomModalType) =>
    (options: AtomModalOptions): AtomModalHandle => {
      nextIdRef.current += 1
      const id = nextIdRef.current
      let resolveResult: (confirmed: boolean) => void = () => undefined
      const result = new Promise<boolean>((resolve) => {
        resolveResult = resolve
      })
      setDialogs((current) => [
        ...current,
        {
          id,
          type,
          options,
          open: true,
          loading: false,
          resolve: resolveResult,
        },
      ])
      return {
        close: () => {
          updateDialog(id, { open: false })
          resolveResult(false)
        },
        result,
      }
    }

  const api: AtomModalApi = {
    confirm: openDialog('confirm'),
    info: openDialog('info'),
    success: openDialog('success'),
    warning: openDialog('warning'),
    error: openDialog('error'),
  }

  const handleOk = async (dialog: AtomModalDialog) => {
    const pending = dialog.options.onOk?.()
    if (pending instanceof Promise) {
      updateDialog(dialog.id, { loading: true })
      try {
        await pending
      } finally {
        updateDialog(dialog.id, { open: false, loading: false })
      }
    } else {
      updateDialog(dialog.id, { open: false })
    }
    dialog.resolve(true)
  }

  const handleCancel = (dialog: AtomModalDialog) => {
    updateDialog(dialog.id, { open: false })
    dialog.resolve(false)
  }

  const contextHolder = dialogs.map((dialog) => (
    <AtomModal
      key={dialog.id}
      open={dialog.open}
      type={dialog.type}
      title={dialog.options.title}
      okText={dialog.options.okText}
      cancelText={dialog.options.cancelText}
      danger={dialog.options.danger}
      width={dialog.options.width}
      confirmLoading={dialog.loading}
      onOk={() => void handleOk(dialog)}
      onCancel={() => handleCancel(dialog)}
      afterClose={() => removeDialog(dialog.id)}
    >
      {dialog.options.content}
    </AtomModal>
  ))

  return [api, contextHolder] as const
}
